import { ErrorType } from '@readcloud/data';
import {
  pdfSelectors,
  readerActions,
  readerSelectors,
  useAppDispatch,
} from '@readcloud/state';
import 'pdfjs-dist/web/pdf_viewer.css';
import React from 'react';
import C from 'classnames';
import { useSelector } from 'react-redux';
import { PDFGrid } from './PDFGrid';

import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { TextSelectionContext } from '../../context/TextSelection';
import { useComponentSize } from '../../hooks/useComponentSize';

import ModalManager from '../Modals/ModalManager';
import { ConnectedLearningToolbar } from '../Toolbars/ConnectedLearningToolbar';
import { PDFPrintIframe } from './PDFPrintIFrame';
import { useLocation } from 'react-router-dom';

interface IPDFViewer {
  documentData: {
    data: any;
    type: string;
    error: ErrorType;
    loading: boolean;
    progress: number;
  };
}

const PDFViewer: React.FC<IPDFViewer> = (props) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const pdfData = useSelector(pdfSelectors.getPDFData);

  const { location, setLocation, pdfDocument, setScale } = React.useContext(
    DocumentWithExtrasContext
  );

  const [initialisedScale, setInitialisedScale] =
    React.useState<boolean>(false);

  const { setCustomTextSelection } = React.useContext(TextSelectionContext);

  const dispatch = useAppDispatch();

  const routerLocation = useLocation();
  const pageLocation = new URLSearchParams(routerLocation.search).get('p');

  React.useEffect(() => {
    //we just want to set the location on load. From there the location is updated in the context first, which updates redux (and the db) using a debounce
    if (!location) {
      setLocation(pageLocation || readerData?.viewState?.location);
    }
  }, [readerData?.viewState?.location]);

  React.useEffect(() => {
    // Only apply the saved scale once a real number arrives. If we call
    // setScale(undefined) we clobber the default of 1 in the context, and every
    // subsequent zoom op (trackpad/pinch/+/-) operates on undefined → NaN.
    const savedScale = readerData?.viewState?.scale;
    if (!initialisedScale && Number.isFinite(savedScale)) {
      setScale(savedScale);
      setInitialisedScale(true);
    }
  }, [readerData?.viewState?.scale]);

  const handlePointerEvent = (e: PointerEvent) => {
    switch (e.pointerType) {
      case 'mouse':
        setCustomTextSelection(false);
        break;
      case 'touch':
        setCustomTextSelection(true);
        break;
      case 'pen':
        setCustomTextSelection(true);
        break;
    }
  };

  document.addEventListener('pointerdown', handlePointerEvent, {
    passive: true,
  });
  document.addEventListener('pointerup', handlePointerEvent, {
    passive: true,
  });

  // Keybinds to navigate a whole page at a time
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      //console.log("ReaderType: ", getReaderType && getReaderType(), controls);
      if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowRight') {
        event.preventDefault();
        dispatch(readerActions.updateDocumentControl({ type: 'next' }));
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowLeft') {
        event.preventDefault();
        dispatch(readerActions.updateDocumentControl({ type: 'prev' }));
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const containerRef = React.useRef<HTMLDivElement>();
  const containerSize = useComponentSize(containerRef);

  return (
    <div
      ref={containerRef}
      className={C(
        'rc-relative reader-scrollbar-fix rc-overflow-auto rc-flex-grow rc-flex',
        pdfData?.settings?.darkMode && 'rc-bg-dark'
      )}
    >
      <PDFGrid
        containerRef={containerRef}
        outerHeight={containerSize.height}
        outerWidth={containerSize.width}
      />
      {(readerData.bookOptions?.printable || readerData.printRights) &&
      readerData.printMode &&
      pdfDocument ? (
        <PDFPrintIframe
          pdfDocument={pdfDocument}
          printRights={readerData.printRights}
          printOptions={readerData.bookOptions?.printable}
          printNumPages={pdfDocument.numPages}
          onClose={() =>
            dispatch(readerActions.updateReaderData({ printMode: false }))
          }
        />
      ) : null}

      <ModalManager />
    </div>
  );
};

export default PDFViewer;
